import { useMemo } from 'react';

export const PAGINATION_ELLIPSIS = 'ellipsis';

export type PaginationItem = number | typeof PAGINATION_ELLIPSIS;

interface IUsePaginationItemsParams {
  currentPage: number;
  numPages: number;
  // Total amount of page buttons, the first and the last page included.
  pageButtonLimit: number;
}

// Builds the list of page buttons, always keeping the first and the last page reachable:
// `1 … 9 10 11 … 100`. An ellipsis is only used when it collapses more than one page,
// otherwise the hidden page is rendered instead. Page indexes are zero based.
export const buildPageItems = (
  currentPage: number,
  numPages: number,
  pageButtonLimit: number
): PaginationItem[] => {
  if (numPages <= pageButtonLimit) {
    return Array.from({ length: numPages }, (_, i) => i);
  }

  const inner = pageButtonLimit - 2;
  const half = Math.floor(inner / 2);
  let start = currentPage - half;
  let end = start + inner - 1;

  if (start < 1) {
    start = 1;
    end = start + inner - 1;
  }

  if (end > numPages - 2) {
    end = numPages - 2;
    start = end - inner + 1;
  }

  const items: PaginationItem[] = [0];

  if (start === 2) {
    items.push(1);
  } else if (start > 2) {
    items.push(PAGINATION_ELLIPSIS);
  }

  for (let i = start; i <= end; i++) {
    items.push(i);
  }

  if (end === numPages - 3) {
    items.push(numPages - 2);
  } else if (end < numPages - 3) {
    items.push(PAGINATION_ELLIPSIS);
  }

  items.push(numPages - 1);

  return items;
};

export const usePaginationItems = ({
  currentPage,
  numPages,
  pageButtonLimit
}: IUsePaginationItemsParams): PaginationItem[] =>
  useMemo(
    () => buildPageItems(currentPage, numPages, pageButtonLimit),
    [currentPage, numPages, pageButtonLimit]
  );
